import config from "@ey/voltron-config";
import { ErrorCodes } from "./UIConstants";

export interface Endpoint {
  serviceUrl: string;
  audience: string;
}

// Legacy EndPoint interface for backward compatibility
export interface EndPoint {
  name: string;
  serviceUrl: string;
  audienceUrl: string;
  isldcupgraded?: boolean;
  datacenterversion?: string;
  isError?: boolean;
  ErrorCode?: ErrorCodes;
}

export interface Settings {
  excelBulkLoadingBatchSize: number;
  RestrictedDatacenterIds: string;
  LogConfig: boolean;
  enableUIValidation?: boolean;
}

export interface EntitySettings {
  SupportedEntityBaseOptions: string;
  NewEntitySupportedOptions?: string;
  SupportedCanvasLDCVersionForNewEntity?: string;
}

export interface ApplicationInsightsConfiguration {
  connectionString: string;
  enableUnhandledPromiseRejectionTracking: string;
}

// Runtime cache for dynamic endpoints (e.g., hercules)
const endPointsCache: EndPoint[] = [];

// Signed-in user's universal identifier, captured once the user preference call returns.
// Combined with the engagement id it derives the worksheet protection password, so that the
// password is reproducible for support without being hardcoded in the bundle.
let currentUserGui = "";

export class Environment {
  // URLs
  static getUniversalApiEndpoint(): Endpoint {
    const serviceUrl = config.url["universalApi"];
    const url = new URL(serviceUrl);
    const audience = config.ad.endpoints[url.origin];
    return { serviceUrl, audience };
  }

  static getHelpUrl(): string {
    return config.url.helpURL;
  }

  static getAtlasUserInfoUrl(): string {
    return config.url.atlasUserInfoUrl;
  }

  static getAtlasManageEngagementUrl(): string {
    return config.url.atlasManageEngagementUrl;
  }

  static getAtlasEditUserInfoUrl(): string {
    return config.url.atlasEditUserInfoUrl;
  }

  // Settings
  static getSettings(): Settings {
    return config.settings;
  }

  static getEntitySettings(): EntitySettings {
    return config.Entitysettings;
  }

  // App Insights
  static getAppInsightsConfig(): ApplicationInsightsConfiguration {
    return config.applicationInsightsConfiguration;
  }

  // Auth
  static getClientId(): string {
    return config.ad.auth.clientId;
  }

  // Endpoint caching
  static getUniversalEndPoint(): EndPoint {
    const { serviceUrl, audience } = this.getUniversalApiEndpoint();
    return {
      name: "universal",
      serviceUrl,
      audienceUrl: audience,
    };
  }

  static findCachedEndPoint(name: string): EndPoint | undefined {
    return endPointsCache.find((ep) => ep.name === name);
  }

  static addEndPointToCache(endPoint: EndPoint): void {
    endPointsCache.push(endPoint);
  }

  static getEndPointsCache(): EndPoint[] {
    return endPointsCache;
  }

  static isValidEndPoint(endPoint: EndPoint | undefined): boolean {
    return !!(endPoint?.name && endPoint?.audienceUrl && endPoint?.serviceUrl);
  }

  // Signed-in user
  static setCurrentUserGui(gui: string | number): void {
    currentUserGui = gui == null ? "" : String(gui);
  }

  static getCurrentUserGui(): string {
    return currentUserGui;
  }

  // Config URL accessor for dynamic property access
  static getConfigUrl(key: string): string {
    return config.url?.[key] ?? config?.[key] ?? "";
  }

  static getFederatedHostName(): string {
    return FEDERATED_HOST_NAME;
  }

  static isDevelopment(): boolean {
    return config?.environment === "development";
  }

  static isDrydockEnabled(): boolean {
    return config?.enableDrydock === true;
  }

  static isSignInDialogEnabled(): boolean {
    return config?.enableSignInDialog === true;
  }
}

// Log on load
if (config.settings?.LogConfig) {
  console.log("CONFIG JSON LOADED", config);
} else {
  console.log("CONFIG JSON LOADED");
}
